import { computed, provide } from 'vue'
import type { MaybeRefOrGetter, Ref } from 'vue'
import type { AgentBranding, AgentPublic, AuthMethods } from 'models'
import { ChatbotProfileContextKey } from 'components/src/chat/chatbotProfileContext'
import { useChatbotProfile } from '~/composables/useChatbotProfile'
import { clearAnonymousUserIdCookie } from '~/utils/cookie'

interface ProfileSessionValue {
    data: {
        user: {
            id: string
            name: string
            email: string
            image?: string | null
            isAnonymous?: boolean | null
        }
    } | null
    refetch: () => Promise<unknown>
}

/**
 * Shared wiring for the in-widget profile/auth surface. Both widgets
 * (embedded, floating) expose the same read-only profile context and the same
 * voluntary sign-in affordance, so the logic lives here to avoid drift.
 *
 * Provides {@link ChatbotProfileContextKey} for the chat components and returns
 * the `hasAuthMethods` / `authBranding` computeds the templates consume.
 */
export function useChatbotProfileProvider(deps: {
    agent: Ref<AgentPublic | undefined>
    branding: Ref<AgentBranding | undefined>
    authMethods: Ref<AuthMethods | undefined>
    session: Ref<ProfileSessionValue>
    signOut: () => Promise<void>
    token: Ref<string>
    baseUrl: string
    headers: MaybeRefOrGetter<Record<string, string> | undefined>
    /** Must match the `cookieName` given to {@link useChatbotAuth}. */
    cookieName?: string
}) {
    const { agent, branding, authMethods, session, signOut, token } = deps

    // Whether the agent has at least one auth method enabled: lets the chat
    // offer a voluntary sign-in to anonymous users (rendered through the
    // `view-auth` slot below).
    const hasAuthMethods = computed(
        () =>
            !!authMethods.value &&
            (authMethods.value.emailPassword ||
                authMethods.value.google ||
                authMethods.value.microsoft),
    )

    // Logo/name for the sign-in form: public agents expose them on the full
    // `agent` (its interface is a superset of the branding one) rather than
    // on `branding`, which only private agents return.
    const authBranding = computed(() =>
        agent.value
            ? {
                  id: agent.value.id,
                  name: agent.value.name,
                  organizationId: agent.value.organizationId,
                  interface: agent.value.interface,
              }
            : branding.value,
    )

    // Expose read-only user info and sign-out to the chat components.
    // Full profile management is handled on the main site via profileUrl.
    provide(
        ChatbotProfileContextKey,
        useChatbotProfile({
            session,
            signOut,
            canSignIn: hasAuthMethods,
            erasure: {
                baseUrl: deps.baseUrl,
                headers: deps.headers,
                // anonymous account deleted with its conversations (B.3):
                // drop the stale cookies, the session watcher then
                // re-establishes a fresh anonymous session
                onAccountDeleted: async () => {
                    clearAnonymousUserIdCookie(deps.cookieName)
                    token.value = ''
                    await session.value.refetch()
                },
            },
        }),
    )

    return { hasAuthMethods, authBranding }
}
